import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import * as pmtiles from 'pmtiles';
import maplibre, {
  AddLayerObject,
  LngLatLike,
  Marker,
  Map as MlMap,
} from 'maplibre-gl';
import { toast } from 'sonner';
import { Feature, GeoJSON, Polygon } from 'geojson';
import { ScoutLocation } from '@/types/location';
import { IMapType, SourceOptions } from '@/types/map';
import {
  initializeMap,
  handleBoundEvents,
  clearStaticLayersAndSources,
  createLayersAndSources,
  buildErrorMessage,
  isLatestBurstActive,
  convertPointToPolygon,
} from '@/utils/scout';
import { colors, hslStringToHex } from '@/utils/styles';
import {
  getBoundsFromLocations,
  getCenter,
  getMapboxAccessToken,
  updateMap,
} from '@/utils/map';
import { Views } from '@/types/view';
import GeoCoder from '@/components/ui/map/Geocoder';
import { Button } from '@/components/shadcn/button';
import { registerLocations } from '@/api/location';
import * as Toast from '@/components/ui/toast/Toast';
import { containsFailures } from '@/utils/validation';
import { useScout } from '@/hooks/useScout';
import {
  addLayer,
  clearSelectedFeaturesMap,
  getSelectedFeaturesMap,
} from '@/components/ui/map/layers/Coverage';
import * as CoverageData from '@/components/ui/map/sources/NewCoverageData';
import * as RoadData from '@/components/ui/map/sources/RoadData';
import { ISelectionPayload } from '@/types/scout';
import { useUser } from '@/hooks/useUser';
import { getSegmentsFromPolygon } from '@/api/segments';
import { beeMarker } from '../../icons/map/markers';

export const MAP_CENTROID_LAYER = 'map-centroid-hm-layer';
export const MAP_GEOJSON_LAYER = 'map-geojson-hm-layer';
export const MAP_GEOJSON_STROKE = 'map-geojson-hm-stroke';
export const MAP_SEGMENTS_LAYER = 'map-segments-hm-layer';
export const MAP_SEGMENTS_STROKE = 'map-segments-hm-stroke';

const MAP_CENTROID_SOURCE = 'map-centroid-hm-source';
const MAP_GEOJSON_SOURCE = 'map-geojson-hm-source';
const MAP_SEGMENTS_SOURCE = 'map-segments-hm-source';

const DEFAULT_ZOOM = 3.74;
const DEFAULT_COORDS: LngLatLike = [-95.77, 34.43];

const MAP_TYPE: IMapType = 'dark';
export interface MapProps {
  activeView: Views;
  locations: ScoutLocation[];
  selectionCallback?: (id: string | number, lastSelectedView: Views) => void;
}

const Map: React.FC<MapProps> = ({
  activeView,
  locations,
  selectionCallback = () => {},
}) => {
  const [map, setMap] = useState<MlMap | null>(null);
  const [name, setName] = useState<string>('');
  const [seen, setSeen] = useState<number>(0);
  const [names, setNames] = useState<string[]>([]);
  const [description, setDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [proximity, setProximity] = useState<[number, number]>();
  const [geometries, setGeometries] = useState<ISelectionPayload | null>();

  const mapContainer = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const fitBounds = useRef(false);
  const locationsLength = useRef<number | null>(null);
  const marker = useRef<Marker | null>(null);

  const { user } = useUser();
  const { setLocations, forceMapUpdate } = useScout();

  useEffect(() => {
    // @ts-ignore
    maplibre.accessToken = getMapboxAccessToken();
    const protocol = new pmtiles.Protocol();
    maplibre.addProtocol('pmtiles', protocol.tile);

    if (
      !initialized.current &&
      mapContainer.current &&
      activeView === Views.Map
    ) {
      initialized.current = true;
      initializeMap({
        mapContainer: mapContainer.current,
        onLoadCallback: newMap => setMap(newMap),
        zoom: DEFAULT_ZOOM,
        center: locations.length > 0 ? getCenter(locations) : DEFAULT_COORDS,
        style: 'mapbox://styles/arielseidman/clyqif1ph03s801prawrb2k27',
      });
    }
  }, [map, activeView, setMap, locations]);

  const handleCallback = useCallback(
    (id: string | number) => {
      selectionCallback && selectionCallback(id, Views.Map);
    },
    [selectionCallback],
  );

  const clearSelection = useCallback(() => {
    if (!map) return;

    const selectedFeaturesMap = getSelectedFeaturesMap();
    const selectedFeatures = Array.from(selectedFeaturesMap.values());
    selectedFeatures?.forEach(({ id, source, sourceLayer }) => {
      map.setFeatureState({ source, sourceLayer, id }, { selected: false });
    });

    clearSelectedFeaturesMap();
    setName('');
    setDescription('');
    setGeometries(null);
  }, [map]);

  const convertCoordinateToLocation = async (lat: number, lon: number) => {
    try {
      const polygon = convertPointToPolygon(`${lon}, ${lat}`);

      if (!polygon) {
        throw new Error('Error converting point to polygon');
      }

      let geojson = polygon;
      let segments;
      let name: string = 'Unnamed Road';

      const { segmentsGeometry, names, error } =
        await getSegmentsFromPolygon(polygon);

      if (error) {
        throw new Error('Error getting segments from polygon');
      }

      if (segmentsGeometry) {
        // create a 20 meter buffer around the merged lines to represent the location
        const feature = turf.buffer(segmentsGeometry, 20, {
          units: 'meters',
        });

        if (feature) {
          geojson = feature.geometry as Polygon;
          segments = segmentsGeometry;
        }
      }

      if (names?.length > 1) {
        const primary =
          names.find((name: string) => name !== 'Unnamed Road') ?? names[0];
        name = `${primary} +${names.length - 1}`;
      } else if (names?.length === 1) {
        name = names[0];
      }

      handleAddLocation(
        {
          polygon: geojson,
          segments,
        },
        name,
      );
    } catch (error) {
      console.error('Error converting point to location.', error);
    }
  };

  const handleAddLocation = useCallback(
    async (geometries: ISelectionPayload, nameOverride?: string) => {
      if (!map || !user) return;

      setIsLoading(true);

      const location = {
        name: nameOverride || name,
        description,
        geojson: geometries.polygon,
        segments: geometries.segments,
      };

      const response = await registerLocations(
        [location],
        user.currentOrganization,
      );

      setIsLoading(false);

      if ('error' in response || !response.locations) {
        toast.custom(t => (
          <Toast.Default
            title="Try Again"
            description="We were unable to add the location, please try again."
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
            ]}
          />
        ));

        return;
      }

      if (containsFailures(response.failures)) {
        const { failures } = response;
        const { errorMessage, reasons } = buildErrorMessage(failures);
        toast.custom(t => (
          <Toast.Default
            title="Some Locations Failed"
            description={
              <>
                {errorMessage}
                <br />
                {reasons}
              </>
            }
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
            ]}
          />
        ));
      }

      if (response.locations.length > 0) {
        toast.custom(t => (
          <Toast.Default
            title="Location Added"
            description="New location successfully added to Scout."
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
              {
                label: 'View Location',
                onClick: () => {
                  handleCallback(response.locations[0]._id);
                  toast.dismiss(t);
                },
                size: 'fw',
              },
            ]}
          />
        ));

        setLocations(prevState => {
          return [...response.locations, ...prevState].sort(
            (a: ScoutLocation, b: ScoutLocation) => {
              const aDate = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
              const bDate = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
              return bDate - aDate;
            },
          );
        });
      }

      clearSelection();
    },
    [
      map,
      name,
      description,
      setLocations,
      handleCallback,
      clearSelection,
      user,
    ],
  );

  const coverageCallback = useCallback(
    (payload: ISelectionPayload | null) => {
      setGeometries(payload);

      const selectedFeaturesMap = getSelectedFeaturesMap();
      const selectedFeatures = Array.from(selectedFeaturesMap.values());
      const names = selectedFeatures.map(({ name }) => name ?? 'Unnamed Road');
      const uniqueNames = new Set(names);
      const seen = selectedFeatures.reduce((acc, { coverage }) => {
        return acc + (coverage ?? 0);
      }, 0);

      setNames(Array.from(uniqueNames));
      setSeen(seen);
    },
    [setGeometries],
  );

  const placeSearchMarker = (map: MlMap, coords: LngLatLike) => {
    if (marker.current) {
      clearSearchMarker();
    } else {
      const markerEl = document.createElement('div');
      markerEl.style.display = 'flex';
      markerEl.innerHTML = beeMarker;

      marker.current = new maplibre.Marker({
        element: markerEl,
        anchor: 'bottom',
        draggable: false,
      });

      marker.current.setLngLat(coords).addTo(map);
    }
  };

  const clearSearchMarker = useCallback(() => {
    if (marker.current) {
      marker.current?.remove();
      marker.current = null;
    }
  }, []);

  useEffect(() => {
    if (names.length > 1) {
      const primary = names.find(name => name !== 'Unnamed Road') ?? names[0];
      setName(`${primary} +${names.length - 1}`);
    } else if (names.length === 1) {
      setName(names[0]);
    }
  }, [names]);

  useEffect(() => {
    if (map) {
      // due to the lastViewed property constantly being updated as users consult the imagery,
      // the map re-renders needlessly. Rather than do an expensive deep comparison, we can
      // simply check the length of the locations array to determine if we need to re-render
      // in the event that the forceMapUpdate flag is not true.
      if (locationsLength.current === locations.length && !forceMapUpdate) {
        return;
      }

      locationsLength.current = locations.length;

      clearStaticLayersAndSources(
        map,
        [
          MAP_GEOJSON_LAYER,
          MAP_GEOJSON_STROKE,
          MAP_SEGMENTS_LAYER,
          MAP_SEGMENTS_STROKE,
          MAP_CENTROID_LAYER,
        ],
        [MAP_SEGMENTS_SOURCE, MAP_GEOJSON_SOURCE, MAP_CENTROID_SOURCE],
      );

      const segmentsCollection: GeoJSON = {
        type: 'FeatureCollection',
        features: locations
          .filter(location => location.segments)
          .map(location => {
            const hasActiveBurst = isLatestBurstActive(location.bursts);

            return {
              type: 'Feature',
              geometry: location.segments,
              properties: {
                id: location._id,
                hasActiveBurst,
              },
            };
          }) as Feature[],
      };

      const geojsonCollection: GeoJSON = {
        type: 'FeatureCollection',
        features: locations
          .filter(location => !location.segments)
          .map(location => {
            const hasActiveBurst = isLatestBurstActive(location.bursts);

            return {
              type: 'Feature',
              geometry: location.geojson,
              properties: {
                id: location._id,
                hasActiveBurst,
              },
            };
          }) as Feature[],
      };

      const centroidCollection: GeoJSON = {
        type: 'FeatureCollection',
        features: locations.map(location => {
          const hasActiveBurst = isLatestBurstActive(location.bursts);

          const centroid = turf.centroid(location.geojson as turf.AllGeoJSON);
          return {
            type: 'Feature',
            properties: {
              id: location._id,
              hasActiveBurst,
            },
            geometry: {
              type: 'Point',
              coordinates: [
                centroid.geometry.coordinates[0],
                centroid.geometry.coordinates[1],
              ],
            },
          };
        }),
      };

      const sourceOptions: SourceOptions[] = [
        {
          id: MAP_SEGMENTS_SOURCE,
          options: {
            type: 'geojson',
            data: segmentsCollection,
            promoteId: 'id',
          },
        },
        {
          id: MAP_GEOJSON_SOURCE,
          options: {
            type: 'geojson',
            data: geojsonCollection,
            promoteId: 'id',
          },
        },
        {
          id: MAP_CENTROID_SOURCE,
          options: {
            type: 'geojson',
            data: centroidCollection,
            promoteId: 'id',
          },
        },
      ];

      const mapLayersOptions: AddLayerObject[] = [
        {
          id: MAP_SEGMENTS_STROKE,
          type: 'line',
          source: MAP_SEGMENTS_SOURCE,
          minzoom: 12,
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': [
              'case',
              ['boolean', ['get', 'hasActiveBurst'], false],
              hslStringToHex(colors.harvestGoldWarning['700']),
              hslStringToHex(colors.scoutBoundary.main),
            ],
            'line-width': 7,
          },
        },
        {
          id: MAP_SEGMENTS_LAYER,
          type: 'line',
          source: MAP_SEGMENTS_SOURCE,
          minzoom: 12,
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': [
              'case',
              ['boolean', ['get', 'hasActiveBurst'], false],
              hslStringToHex(colors.harvestGoldWarning['600']),
              hslStringToHex(colors.scoutBoundary.light),
            ],
            'line-width': 3,
          },
        },
        {
          id: MAP_GEOJSON_LAYER,
          type: 'fill',
          source: MAP_GEOJSON_SOURCE,
          minzoom: 12,
          layout: {},
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['get', 'hasActiveBurst'], false],
              hslStringToHex(colors.harvestGoldWarning['600']),
              hslStringToHex(colors.scoutBoundary.light),
            ],
            'fill-opacity': 1,
          },
        },
        {
          id: MAP_GEOJSON_STROKE,
          type: 'line',
          source: MAP_GEOJSON_SOURCE,
          minzoom: 12,
          layout: {},
          paint: {
            'line-color': [
              'case',
              ['boolean', ['get', 'hasActiveBurst'], false],
              hslStringToHex(colors.harvestGoldWarning['700']),
              hslStringToHex(colors.scoutBoundary.main),
            ],
            'line-width': 2,
          },
        },
        {
          id: MAP_CENTROID_LAYER,
          type: 'circle',
          source: MAP_CENTROID_SOURCE,
          minzoom: 0,
          maxzoom: 12,
          layout: {},
          paint: {
            'circle-color': [
              'case',
              ['boolean', ['get', 'hasActiveBurst'], false],
              hslStringToHex(colors.harvestGoldWarning['600']),
              hslStringToHex(colors.scoutBoundary.light),
            ],
            'circle-radius': 4,
            'circle-pitch-scale': 'viewport',
          },
        },
      ];

      createLayersAndSources(map, mapLayersOptions, sourceOptions);
      handleBoundEvents(
        map,
        [
          MAP_SEGMENTS_LAYER,
          MAP_SEGMENTS_STROKE,
          MAP_GEOJSON_LAYER,
          MAP_CENTROID_LAYER,
        ],
        handleCallback,
      );
    }
  }, [map, locations, handleCallback, forceMapUpdate]);

  useEffect(() => {
    if (!map || fitBounds.current) return;

    if (locations.length > 0) {
      fitBounds.current = true;
      const bounds = getBoundsFromLocations(locations);
      map.fitBounds(bounds, { padding: 100, animate: false });
    }
  }, [map, locations]);

  useEffect(() => {
    if (map) {
      CoverageData.addSource(map);
      RoadData.addSource(map);

      addLayer(
        map,
        {
          type: 'scout',
          beforeId: {
            selection: 'road-label',
            outline: 'building number label',
          },
          zoomThreshold: 15,
          callback: coverageCallback,
        },
        RoadData.SOURCE_ID,
      );

      addLayer(
        map,
        {
          type: 'scout',
          beforeId: {
            selection: 'road-label',
            outline: 'building number label',
          },
          zoomThreshold: 15,
          callback: coverageCallback,
        },
        CoverageData.SOURCE_ID,
      );
    }
  }, [map, coverageCallback]);

  useEffect(() => {
    if (map) {
      map.on('moveend', () => {
        const center = map.getCenter();
        setProximity([center.lng, center.lat]);
      });
    }
  }, [map]);

  return (
    <div
      data-type={MAP_TYPE}
      className="group/mainMap relative h-[calc(100dvh-248px)] w-full overflow-hidden rounded-lg lg:h-[calc(100dvh-215px)]">
      <div ref={mapContainer} id="scout-map" className="relative size-full" />

      <div className="absolute left-4 top-4 z-10">
        <GeoCoder
          proximity={proximity}
          placeholder="Search the map"
          onSelected={({ lat, lon, zoom, bbox, isCoordinate }) => {
            if (map) {
              if (isCoordinate) {
                convertCoordinateToLocation(lat, lon);
                updateMap(map, lat, lon, 17, [0, 0], 10, bbox);
              } else {
                placeSearchMarker(map, [lon, lat]);
                updateMap(map, lat, lon, zoom, [0, 0], 2, bbox);
              }
            }
          }}
          onClear={clearSearchMarker}
        />
      </div>
      <div className="absolute bottom-4 left-1/2 max-w-[70%] -translate-x-1/2">
        {geometries && (
          <div className="flex h-[50px] flex-col items-start justify-center gap-4 rounded-lg bg-neutral-1000 px-2.5 py-4 shadow">
            <div className="flex items-center justify-center gap-5">
              <div className="hidden items-center justify-start gap-5 pl-2.5 sm:flex">
                <div className="truncate font-bold leading-none">{name}</div>
                <div className="hidden truncate font-bold leading-none lg:flex">
                  {seen} drives
                </div>
              </div>

              <div className="flex items-center justify-start gap-2.5">
                <Button variant="dark" onClick={() => clearSelection()}>
                  Clear
                </Button>
                <Button
                  size="fw"
                  disabled={!geometries.isContiguous || isLoading}
                  onClick={() => {
                    if (geometries) handleAddLocation(geometries);
                  }}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Map;
